import React, { ComponentProps, useEffect, useState } from 'react';
import { asView } from '../../../_lib/ViewFetcher';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { UserProfile } from '../../../types/api/UserProfile';
import { PostBubble } from '../../../_lib/components/Posts/partials/PostBubble';
import { Option, Select } from '../../../_lib/Form2';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import ContentMessage from '../../../_lib/components/ContentMessage/ContentMessage';
import { FooterLink } from '../../../components/molecules/FooterLink/FooterLink';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ILink from '../../../types/ILink';
import { ThreadLastActivityInfo } from '../../ThreadList/partials/ThreadLastActivityInfo';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';

type Thread = {
  id: string;
  createDate: string;
  title: string;
  pageLink: ILink;
  lastActivityInfo: {
    author: UserProfile;
    activityDate: string;
    activityUrl: string;
    resourceReferencesToToUrls: any;
    content: string;
  };
};

type Forum = {
  id: string;
  title: string;
  url: string;
  threads: Thread[];
};

export type LatestForumPostsDefaultViewProps = { forums?: Forum[] };

export function LatestForumPostsDefault({ forums }: LatestForumPostsDefaultViewProps) {
  const { t } = useSonTranslation('LatestForumPosts');
  const controlId = useHtmlValidId();

  const [activeForum, setActiveForum] = useState<Forum | undefined>(forums ? forums[0] : undefined);

  useEffect(() => {
    setActiveForum(forums ? forums[0] : undefined);
  }, [JSON.stringify(forums)]);

  if (!forums || forums.length === 0) {
    return <ContentMessage text={t('noForumAdded')} />;
  }

  const setActiveForumById = (id: string) => {
    const foundForum = forums?.find((forum: Forum) => forum.id === id);
    if (foundForum) {
      setActiveForum(foundForum);
    }
  };

  return (
    <>
      <div className="latest-forum-posts__select">
        <Select
          key={forums.length} // key here so select resets when new feeds are added
          label={t('select')}
          labelHidden
          aria-controls={controlId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setActiveForumById(e.target.value)}
        >
          {forums.map(forum => (
            <Option key={forum.id} value={forum.id} selected={forum.id === activeForum?.id}>
              {forum.title}
            </Option>
          ))}
        </Select>
      </div>
      <div id={controlId} className="latest-forum-posts__container">
        <ContentList>
          {activeForum?.threads.map((thread: Thread) => (
            <ContentListItem key={thread.id}>
              <ContentHeadline title={thread.title} hierarchy={3} url={thread.lastActivityInfo.activityUrl} />
              <PostBubble
                model={thread.lastActivityInfo as any}
                modifier={['round', 'small-margin-bottom']}
                smallText
                verySmallPadding
              />
              <ThreadLastActivityInfo lastActivityInfo={thread.lastActivityInfo} />
            </ContentListItem>
          ))}
        </ContentList>
      </div>
      {activeForum ? <FooterLink footerButtonLabel={t('footerButton')} footerLink={{ url: activeForum.url }} /> : null}
    </>
  );
}
export const LatestForumPostsDefaultView = asView(LatestForumPostsDefault);
export type LatestForumPostsDefaultView = ComponentProps<typeof LatestForumPostsDefaultView>;
